import { useState, useEffect, type CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, FolderOpen, Bot, Settings, ListChecks, Moon, Sun, PanelLeftClose, PanelLeftOpen, ChevronRight,
} from 'lucide-react'
import styles from './AppShell.module.css'

export interface AppShellUser {
  name: string | null | undefined
  role: string
}

export interface AppShellProps {
  navMode: 'client' | 'admin'
  user?: AppShellUser | null
  children: React.ReactNode
}

const CLIENT_NAV = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/projects/new', label: 'New Project', Icon: FolderOpen },
  { to: '/symon', label: 'Symon', Icon: Bot },
  { to: '/settings', label: 'Settings', Icon: Settings },
]

const ADMIN_NAV = [
  { to: '/admin/projects', label: 'Projects', Icon: FolderOpen },
  { to: '/admin/projects', label: 'Activity Log', Icon: ListChecks },
]

function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return name.split(' ').map(p => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?'
}

export function AppShell({ navMode, user, children }: AppShellProps) {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('rb_theme') as 'dark' | 'light') ?? 'dark'
  })

  const [open, setOpen] = useState<boolean>(() => {
    const stored = sessionStorage.getItem('rb_client_sidebar_open')
    return stored === null ? true : stored === 'true'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('rb_theme', theme)
  }, [theme])

  function toggleTheme() {
    setTheme(t => t === 'dark' ? 'light' : 'dark')
  }

  function toggleSidebar() {
    setOpen(prev => {
      const next = !prev
      sessionStorage.setItem('rb_client_sidebar_open', String(next))
      return next
    })
  }

  const navItems = navMode === 'client' ? CLIENT_NAV : ADMIN_NAV
  const sidebarW = open ? 'var(--nav-rail-width)' : '56px'

  return (
    <div className={styles.shell} style={{ '--_sidebar-w': sidebarW } as CSSProperties}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerBrand} style={{ width: sidebarW }}>
          <span className={styles.mark}>
            <svg viewBox="0 0 36 36" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11.5 12.5a5.5 5.5 0 1 0 0 11c2.5 0 4-1.6 6.5-5.5 2.5-3.9 4-5.5 6.5-5.5a5.5 5.5 0 1 1 0 11c-2.5 0-4-1.6-6.5-5.5" />
            </svg>
          </span>
          {open && (
            <>
              <span className={styles.wordmark}>energy<b>OS</b></span>
              <span className={styles.ver}>Sigi</span>
            </>
          )}
        </div>

        <div className={styles.headerRight}>
          <button className={styles.themeToggle} onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          {user && (
            <div className={styles.headerAvatar} title={user.name ?? undefined}>
              {initials(user.name)}
            </div>
          )}
        </div>
      </header>

      {/* Sidebar */}
      <aside className={open ? styles.sidenav : `${styles.sidenav} ${styles.sidenavCollapsed}`}>
        <div className={styles.section}>
          <div className={open ? styles.eyebrow : `${styles.eyebrow} ${styles.eyebrowHidden}`}>
            {navMode === 'client' ? 'Navigation' : 'Admin'}
          </div>
          {navItems.map(({ to, label, Icon }) => (
            <NavLink
              key={label}
              to={to}
              className={({ isActive }) =>
                [
                  styles.navItem,
                  !open && styles.navItemCollapsed,
                  isActive && styles.navItemActive,
                ].filter(Boolean).join(' ')
              }
              title={!open ? label : undefined}
            >
              <span className={styles.navIcon}><Icon size={18} strokeWidth={1.5} /></span>
              <span className={open ? styles.navLabel : `${styles.navLabel} ${styles.navLabelHidden}`}>
                {label}
              </span>
            </NavLink>
          ))}
        </div>

        <div className={styles.sidenavFooter}>
          <button
            className={open ? styles.collapseBtn : `${styles.collapseBtn} ${styles.collapseBtnCollapsed}`}
            onClick={toggleSidebar}
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className={styles.navIcon}>
              {open ? <PanelLeftClose size={18} strokeWidth={1.5} /> : <PanelLeftOpen size={18} strokeWidth={1.5} />}
            </span>
            {open && <span>Collapse</span>}
          </button>

          {user && (
            <div className={open ? styles.userRow : `${styles.userRow} ${styles.navItemCollapsed}`} title={user.name ?? undefined}>
              <div className={styles.avatar}>{initials(user.name)}</div>
              <div className={open ? styles.userInfo : `${styles.userInfo} ${styles.userInfoHidden}`}>
                <span className={styles.userName}>{user.name ?? ''}</span>
                <span className={styles.userRole}>{user.role}</span>
              </div>
              {open && <ChevronRight size={14} strokeWidth={1.5} style={{ color: 'var(--fg-subtle)', flexShrink: 0 }} />}
            </div>
          )}
        </div>
      </aside>

      {/* Content */}
      <main className={styles.content}>
        {children}
      </main>
    </div>
  )
}
